import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ExercisePickerSheet, loadExerciseLibrary } from '../components/workout/ExercisePickerSheet';
import { ExerciseSessionCard } from '../components/workout/ExerciseSessionCard';
import { CardioSessionPanel, type EquipmentRow } from '../components/workout/CardioSessionPanel';
import { TrackingLevelSheet } from '../components/workout/TrackingLevelSheet';
import { RestTimerHost } from '../components/workout/RestTimer';
import { PrimaryButton, PageBackButton, BottomSheet } from '../components/premium';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError } from '../lib/api';
import { dateAndTimeToIso, datetimeLocalToIso, isoToDateAndTime, isoToDatetimeLocal } from '../lib/datetimeLocal';
import { getTrainingPrefs } from '../lib/trainingPrefs';
import {
  WORKOUT_ACTIVITY_TYPES,
  isCardioActivityType,
  normalizeActivityType,
} from '../lib/workoutActivity';
import { estimateSmartCardioCalories } from '../lib/cardioCalories';
import {
  cardioWarnings,
  defaultLevelForActivity,
  metersFromDistanceInput,
  normalizeTrackingLevel,
  type Physiology,
  type TrackingLevel,
} from '../lib/cardioTracking';
import { draftsFromWorkout, emptySegment, segmentToPayload, type DraftSegment } from '../lib/cardioDraft';
import {
  buildPreviewPayload,
  defaultSessionState,
  estimateSessionKcal,
  exerciseFromName,
  hapticSuccess,
  sessionHasWork,
  sessionStartedToIso,
  type DraftExercise,
} from '../lib/workoutSession';
import {
  clearWorkoutSessionDraft,
  draftHasWork,
  loadWorkoutSessionDraft,
  saveWorkoutSessionDraft,
} from '../lib/workoutSessionDraft';
import { workoutSetCount, workoutExerciseCount, formatVolume } from '../lib/workoutStats';
import { workoutDayStreakFromIso } from '../lib/workoutStreak';
import type { UserProfile, Workout } from '../types';

type Phase = 'logging' | 'complete';

type CompleteSummary = {
  name: string;
  durationMin: number | null;
  exercises: number;
  sets: number;
  volume: string | null;
  kcal: number;
  streak: number;
};

function elapsedMinutes(startedAt: number): number {
  return Math.max(1, Math.round((Date.now() - startedAt) / 60000));
}

function StartedAtFields({
  value,
  onChange,
}: {
  value: string;
  onChange: (nextLocal: string) => void;
}) {
  const iso = datetimeLocalToIso(value) ?? new Date().toISOString();
  const { date, time } = isoToDateAndTime(iso);

  function apply(nextDate: string, nextTime: string) {
    const nextIso = dateAndTimeToIso(nextDate || date, nextTime || time || '12:00');
    if (nextIso) onChange(isoToDatetimeLocal(nextIso));
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="block min-w-0 space-y-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">Date</span>
        <input
          type="date"
          value={date}
          onChange={(e) => apply(e.target.value, time)}
          className="w-full min-w-0 rounded-xl border border-white/10 bg-black/30 px-2 py-2 text-sm text-[var(--text-primary)] outline-none"
        />
      </label>
      <label className="block min-w-0 space-y-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">Time</span>
        <input
          type="time"
          value={time}
          onChange={(e) => apply(date, e.target.value)}
          className="w-full min-w-0 rounded-xl border border-white/10 bg-black/30 px-2 py-2 text-sm text-[var(--text-primary)] outline-none"
        />
      </label>
    </div>
  );
}

export function WorkoutSessionPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { editId } = useParams();
  const { user } = useAuth();
  const prefersMetric = user?.preferredUnitSystem !== 'imperial';
  const imperial = !prefersMetric;
  const trainingPrefs = getTrainingPrefs(user?.id);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [editWorkout, setEditWorkout] = useState<Workout | null>(null);
  const [loading, setLoading] = useState(Boolean(editId));
  const [completeSummary, setCompleteSummary] = useState<CompleteSummary | null>(null);
  const [phase, setPhase] = useState<Phase>('logging');

  const [workoutName, setWorkoutName] = useState('');
  const [activityType, setActivityType] = useState('weightlifting');
  const [sessionStartedLocal, setSessionStartedLocal] = useState(() => isoToDatetimeLocal(new Date().toISOString()));
  const [durationMinutesStr, setDurationMinutesStr] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [exercises, setExercises] = useState<DraftExercise[]>([]);
  const [cardioSegments, setCardioSegments] = useState<DraftSegment[]>(() => [emptySegment('outdoor_walk')]);
  const [trackingLevel, setTrackingLevel] = useState<TrackingLevel>(() => defaultLevelForActivity('weightlifting'));
  const [askTracking, setAskTracking] = useState(false);
  const trackingByActivity = useRef<Record<string, { level: TrackingLevel; asked: boolean }>>({});
  const [trackingSheetOpen, setTrackingSheetOpen] = useState(false);
  const [physiology, setPhysiology] = useState<Physiology | null>(null);
  const [equipment, setEquipment] = useState<EquipmentRow[]>([]);
  const [equipmentId, setEquipmentId] = useState('');
  const [caloriesOverride, setCaloriesOverride] = useState('');
  const [perceivedEffort, setPerceivedEffort] = useState('');
  const [headerOpen, setHeaderOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finishSheetOpen, setFinishSheetOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sessionStartMs] = useState(() => Date.now());
  const [tick, setTick] = useState(0);
  const [restActive, setRestActive] = useState(false);

  const [exerciseBuiltIn, setExerciseBuiltIn] = useState<string[]>([]);
  const [exerciseCustom, setExerciseCustom] = useState<string[]>([]);

  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    void loadExerciseLibrary().then((d) => {
      setExerciseBuiltIn(d.builtIn ?? []);
      setExerciseCustom(d.custom ?? []);
    });
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const [p, w, phys, gear] = await Promise.all([
          api<UserProfile | null>('/api/profile'),
          api<Workout[]>('/api/workouts'),
          api<Physiology>('/api/tracking/physiology').catch(() => null),
          api<EquipmentRow[]>('/api/tracking/equipment').catch(() => []),
        ]);
        setProfile(p);
        setWorkouts(w);
        if (phys) setPhysiology(phys);
        setEquipment(gear);
        if (editId) {
          const found = w.find((x) => x.id === editId);
          setEditWorkout(found ?? null);
          if (found) {
            const d = defaultSessionState(found, imperial);
            setWorkoutName(d.workoutName);
            setActivityType(d.activityType);
            setSessionStartedLocal(d.sessionStartedLocal);
            setDurationMinutesStr(d.durationMinutesStr);
            setSessionNotes(d.sessionNotes);
            setExercises(d.exercises);
            setCardioSegments(draftsFromWorkout(found, imperial));
            setPerceivedEffort(found.perceivedEffort != null ? String(found.perceivedEffort) : '');
            setCaloriesOverride(found.caloriesOverride != null ? String(found.caloriesOverride) : '');
            setEquipmentId(found.equipmentId ?? '');
          }
        } else {
          const state = location.state as { prefillName?: string; prefillExercises?: string[] } | null;
          if (state?.prefillExercises?.length) {
            setWorkoutName(state.prefillName ?? '');
            setExercises(state.prefillExercises.map((name) => exerciseFromName(name, w, imperial)));
          } else if (state?.prefillName) {
            setWorkoutName(state.prefillName);
          } else {
            const draft = loadWorkoutSessionDraft(user?.id);
            if (draft && draftHasWork(draft)) {
              setWorkoutName(draft.workoutName);
              setActivityType(draft.activityType);
              setSessionStartedLocal(draft.sessionStartedLocal);
              setDurationMinutesStr(draft.durationMinutesStr);
              setSessionNotes(draft.sessionNotes);
              setExercises(draft.exercises);
            }
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [editId, imperial, location.state, user?.id]);

  useEffect(() => {
    let cancelled = false;
    const act = activityType;
    const cached = trackingByActivity.current[act];
    if (cached) {
      setTrackingLevel(cached.level);
      setAskTracking(!editId && !cached.asked);
    } else {
      setTrackingLevel(defaultLevelForActivity(act));
    }
    void (async () => {
      try {
        const data = await api<{ activityType: string; level: TrackingLevel | null; asked: boolean }>(
          `/api/tracking?activityType=${encodeURIComponent(act)}`,
        );
        if (cancelled) return;
        const level = normalizeTrackingLevel(data.level ?? defaultLevelForActivity(act), act);
        trackingByActivity.current[act] = { level, asked: data.asked };
        setTrackingLevel(level);
        setAskTracking(!editId && !data.asked);
      } catch {
        if (!cancelled) {
          const level = trackingByActivity.current[act]?.level ?? defaultLevelForActivity(act);
          setTrackingLevel(level);
          setAskTracking(!editId && !trackingByActivity.current[act]?.asked);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activityType, user?.id, editId]);

  useEffect(() => {
    if (editId || loading) return;
    const t = window.setTimeout(() => {
      saveWorkoutSessionDraft(user?.id, {
        workoutName,
        activityType,
        sessionStartedLocal,
        durationMinutesStr,
        sessionNotes,
        exercises,
        sessionStartMs,
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    editId,
    loading,
    user?.id,
    workoutName,
    activityType,
    sessionStartedLocal,
    durationMinutesStr,
    sessionNotes,
    exercises,
    sessionStartMs,
  ]);

  const cardioMode = isCardioActivityType(activityType);
  const elapsed = elapsedMinutes(sessionStartMs);
  void tick;

  const primarySeg = cardioSegments[0];
  const durationForKcal = durationMinutesStr ? Math.round(Number(durationMinutesStr)) : elapsed;
  const previewKcal = useMemo(() => {
    if (cardioMode) {
      return estimateSmartCardioCalories({
        activityType,
        durationMinutes: Number.isFinite(durationForKcal) ? durationForKcal : null,
        distanceMeters: metersFromDistanceInput(primarySeg?.distance ?? '', imperial),
        elevGainM: Number(primarySeg?.elevGain) || null,
        avgHr: Number(primarySeg?.avgHr) || null,
        bodyWeightKg: profile?.weightKg,
        age: profile?.age,
        sex: profile?.sex,
        caloriesOverride: Number(caloriesOverride) || null,
      }).kcal;
    }
    return estimateSessionKcal(activityType, exercises, profile?.weightKg, durationForKcal, prefersMetric);
  }, [
    cardioMode,
    activityType,
    exercises,
    profile,
    durationForKcal,
    prefersMetric,
    primarySeg?.distance,
    primarySeg?.elevGain,
    primarySeg?.avgHr,
    imperial,
    caloriesOverride,
  ]);

  const durationWarns = cardioWarnings({
    durationMinutes: Number.isFinite(durationForKcal) ? durationForKcal : null,
    distanceMeters: metersFromDistanceInput(primarySeg?.distance ?? '', imperial),
    avgHr: Number(primarySeg?.avgHr) || null,
    maxHr: Number(primarySeg?.maxHr) || null,
    activityType,
  });

  const canFinish = (sessionHasWork(exercises, cardioMode) || sessionNotes.trim().length > 0) && !saving;

  const recentExercises = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const w of workouts) {
      for (const ex of w.exercises) {
        const n = ex.name.trim();
        const k = n.toLowerCase();
        if (!n || seen.has(k)) continue;
        seen.add(k);
        out.push(n);
        if (out.length >= 12) return out;
      }
    }
    return out;
  }, [workouts]);

  const onExerciseCustomAdded = useCallback(async (canonicalName: string) => {
    const canon = canonicalName.trim();
    if (!canon) return;
    try {
      await api('/api/exercises/custom', {
        method: 'POST',
        body: JSON.stringify({ name: canon }),
      });
    } catch {
      /* ignore */
    }
    setExerciseCustom((prev) => {
      if (prev.some((x) => x.toLowerCase() === canon.toLowerCase())) return prev;
      return [...prev, canon].sort((a, b) => a.localeCompare(b, 'en'));
    });
  }, []);

  function applyActivityType(nextRaw: string) {
    const next = normalizeActivityType(nextRaw);
    if (next === activityType) return;
    setActivityType(next);
    setTrackingSheetOpen(false);
    const cached = trackingByActivity.current[next];
    setTrackingLevel(cached?.level ?? defaultLevelForActivity(next));
    setAskTracking(false);
    if (isCardioActivityType(next)) {
      if (next === 'multisport') {
        setCardioSegments([emptySegment('swim'), emptySegment('other_cardio', true), emptySegment('outdoor_ride')]);
      } else {
        setCardioSegments([emptySegment(next)]);
      }
    }
  }

  async function saveTracking(level: TrackingLevel, forActivity = activityType) {
    const act = normalizeActivityType(forActivity);
    const lvl = normalizeTrackingLevel(level, act);
    trackingByActivity.current[act] = { level: lvl, asked: true };
    if (act === activityType) {
      setTrackingLevel(lvl);
      setAskTracking(false);
      setTrackingSheetOpen(false);
    }
    try {
      await api('/api/tracking', {
        method: 'PUT',
        body: JSON.stringify({ activityType: act, level: lvl }),
      });
    } catch {
      /* still use local level for this activity */
    }
  }

  function addExerciseByName(name: string) {
    const ex = exerciseFromName(name, workouts, imperial);
    setExercises((prev) => [...prev, ex]);
    void onExerciseCustomAdded(name);
  }

  function openFinishSheet() {
    if (!canFinish) return;
    if (!durationMinutesStr) setDurationMinutesStr(String(elapsed));
    setFinishSheetOpen(true);
  }

  async function finishWorkout() {
    if (!canFinish) return;
    setFinishSheetOpen(false);
    setSaving(true);
    setSaveError(null);
    const parsedDuration = durationMinutesStr
      ? Math.min(1440, Math.max(1, Math.round(Number(durationMinutesStr))))
      : elapsed;
    const sessionIso = sessionStartedLocal.trim()
      ? sessionStartedToIso(sessionStartedLocal)
      : new Date().toISOString();
    const payload = buildPreviewPayload(exercises, prefersMetric, profile?.weightKg);
    const cardioPayload = cardioMode
      ? cardioSegments.map((s, i) => segmentToPayload(s, imperial, parsedDuration, i))
      : [];
    const fromSegs = cardioPayload.reduce((s, x) => s + (x.durationSeconds ?? 0), 0);
    const durationMinutes = fromSegs > 0 ? Math.max(1, Math.round(fromSegs / 60)) : parsedDuration;
    if (physiology && cardioMode && trackingLevel === 'full') {
      void api('/api/tracking/physiology', {
        method: 'PUT',
        body: JSON.stringify(physiology),
      }).catch(() => {});
    }

    try {
      let saved: Workout;
      const body = {
        activityType,
        name:
            workoutName.trim() ||
          (activityType === 'multisport'
            ? 'Brick'
            : cardioMode
            ? `Cardio · ${WORKOUT_ACTIVITY_TYPES.find((a) => a.id === activityType)?.label ?? activityType}`
            : `Workout · ${new Date().toLocaleDateString()}`),
        startedAt: sessionIso,
        durationMinutes,
        notes: sessionNotes.trim() || null,
        exercises: cardioMode ? [] : payload,
        cardioSegments: cardioPayload,
        perceivedEffort: perceivedEffort ? Number(perceivedEffort) : null,
        caloriesOverride: caloriesOverride ? Number(caloriesOverride) : null,
        equipmentId: equipmentId || null,
      };
      if (editId) {
        saved = await api<Workout>(`/api/workouts/${editId}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        saved = await api<Workout>('/api/workouts', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      hapticSuccess();
      clearWorkoutSessionDraft(user?.id);
      const allDates = [...workouts.filter((w) => w.id !== saved.id).map((w) => w.startedAt), saved.startedAt];
      setCompleteSummary({
        name: saved.name,
        durationMin: saved.durationMinutes,
        exercises: workoutExerciseCount(saved),
        sets: workoutSetCount(saved),
        volume: formatVolume(saved, imperial),
        kcal: saved.estimatedCaloriesBurned ?? previewKcal,
        streak: workoutDayStreakFromIso(allDates),
      });
      setPhase('complete');
    } catch (e) {
      if (e instanceof ApiError) {
        try {
          const j = JSON.parse(e.body ?? e.message) as { error?: string };
          setSaveError(j?.error ?? e.message);
        } catch {
          setSaveError(e.message);
        }
      } else {
        setSaveError('Could not save workout.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="app-body flex min-h-screen items-center justify-center text-sm text-[var(--text-muted)]">
        Loading session…
      </div>
    );
  }

  if (phase === 'complete' && completeSummary) {
    return (
      <div className="app-body fixed inset-0 z-50 flex min-h-screen flex-col items-center justify-center px-6 pb-[env(safe-area-inset-bottom)] pt-[max(1rem,env(safe-area-inset-top))]">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          className="premium-card-hero w-full max-w-md !p-8 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.1 }}
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/20 text-3xl ring-2 ring-emerald-400/40"
          >
            ✓
          </motion.div>
          <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">Workout complete</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">{completeSummary.name}</p>
          <div className="mt-6 grid grid-cols-2 gap-3 text-left">
            {completeSummary.durationMin ? (
              <div className="rounded-xl bg-white/[0.05] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Duration</div>
                <div className="font-display text-lg font-bold">{completeSummary.durationMin} min</div>
              </div>
            ) : null}
            {completeSummary.sets > 0 ? (
            <div className="rounded-xl bg-white/[0.05] px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Sets</div>
              <div className="font-display text-lg font-bold">{completeSummary.sets}</div>
            </div>
            ) : null}
            {completeSummary.exercises > 0 ? (
            <div className="rounded-xl bg-white/[0.05] px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Exercises</div>
              <div className="font-display text-lg font-bold">{completeSummary.exercises}</div>
            </div>
            ) : null}
            <div className="rounded-xl bg-white/[0.05] px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Burn</div>
              <div className="font-display text-lg font-bold">~{completeSummary.kcal} kcal</div>
            </div>
            {completeSummary.volume ? (
              <div className="col-span-2 rounded-xl bg-white/[0.05] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Volume</div>
                <div className="font-display text-lg font-bold">{completeSummary.volume}</div>
              </div>
            ) : null}
            {completeSummary.streak > 1 ? (
              <div className="col-span-2 rounded-xl border border-amber-400/25 bg-amber-500/10 px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wide text-amber-300/80">Streak</div>
                <div className="font-display text-lg font-bold text-amber-200">{completeSummary.streak} days</div>
              </div>
            ) : null}
          </div>
          <PrimaryButton className="mt-8" fullWidth onClick={() => nav('/workouts', { replace: true })}>
            Done
          </PrimaryButton>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="app-body fixed inset-0 z-50 flex h-[100dvh] flex-col bg-[#070A12]">
      <header className="shrink-0 border-b border-white/8 bg-[#070A12]/95 px-4 pb-3 pt-[calc(env(safe-area-inset-top,0px)+0.375rem)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <PageBackButton onClick={() => nav('/workouts')} label="Back to Train" />
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-lg font-bold text-[var(--text-primary)]">
              {editId ? 'Edit workout' : 'New workout'}
            </h1>
            <p className="text-xs tabular-nums text-[var(--text-muted)]">{elapsed} min</p>
          </div>
          <button
            type="button"
            disabled={!canFinish}
            onClick={openFinishSheet}
            className="rounded-xl bg-accent px-4 py-2 text-sm font-bold text-white disabled:opacity-40"
          >
            {saving ? '…' : 'Finish'}
          </button>
        </div>
      </header>

      <div className="nice-scroll mx-auto w-full max-w-lg min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <label className="mb-3 block">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
            What did you do?
          </span>
          <select
            value={activityType}
            onChange={(e) => applyActivityType(e.target.value)}
            className="mt-1 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none"
          >
            {WORKOUT_ACTIVITY_TYPES.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => setHeaderOpen((o) => !o)}
          className="mb-3 flex w-full flex-wrap items-center gap-2 rounded-2xl border border-white/8 bg-white/[0.03] px-3 py-2.5 text-left"
        >
          <span className="rounded-full bg-white/[0.06] px-2.5 py-0.5 text-[11px] text-[var(--text-muted)]">
            ~{previewKcal} kcal
          </span>
          <span className="ml-auto text-xs text-[var(--text-muted)]">{headerOpen ? '▲' : '▼'} Details</span>
        </button>

        {headerOpen ? (
          <div className="mb-4 space-y-2 rounded-2xl border border-white/8 bg-[#121a2e] p-3">
            <input
              value={workoutName}
              onChange={(e) => setWorkoutName(e.target.value)}
              placeholder="Session name"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-[var(--text-primary)] outline-none"
            />
            <div className="space-y-2">
              <StartedAtFields value={sessionStartedLocal} onChange={setSessionStartedLocal} />
              {!cardioMode ? (
              <input
                inputMode="numeric"
                value={durationMinutesStr}
                onChange={(e) => setDurationMinutesStr(e.target.value)}
                placeholder="Duration min"
                className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-[var(--text-primary)] outline-none"
              />
              ) : null}
            </div>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                Session notes
              </span>
              <textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                rows={3}
                placeholder="How you feel — energy, soreness, sick, sleep, etc."
                className="mt-1 w-full resize-y rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
              />
            </label>
          </div>
        ) : null}

        {!headerOpen ? (
          <label className="mb-4 block">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
              How are you feeling?
            </span>
            <textarea
              value={sessionNotes}
              onChange={(e) => setSessionNotes(e.target.value)}
              rows={2}
              placeholder="Optional — sick, tired, great energy…"
              className="mt-1 w-full resize-none rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
            />
          </label>
        ) : null}

        {cardioMode ? (
          <CardioSessionPanel
            activityType={activityType}
            level={trackingLevel}
            onOpenTracking={() => setTrackingSheetOpen(true)}
            durationMinutesStr={durationMinutesStr}
            onDurationChange={setDurationMinutesStr}
            segments={cardioSegments}
            onSegmentsChange={setCardioSegments}
            imperial={imperial}
            profile={profile}
            physiology={physiology}
            onPhysiologyChange={setPhysiology}
            equipment={equipment}
            equipmentId={equipmentId}
            onEquipmentId={setEquipmentId}
            onAddEquipment={(kind, name) => {
              void api<EquipmentRow>('/api/tracking/equipment', {
                method: 'POST',
                body: JSON.stringify({ kind, name }),
              }).then((row) => {
                setEquipment((prev) => [{ id: row.id, kind: row.kind, name: row.name, distanceMeters: 0 }, ...prev]);
                setEquipmentId(row.id);
              });
            }}
            caloriesOverride={caloriesOverride}
            onCaloriesOverride={setCaloriesOverride}
            perceivedEffort={perceivedEffort}
            onPerceivedEffort={setPerceivedEffort}
          />
        ) : (
          <div className="mb-3 flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.03] px-3 py-2">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                Tracking · this activity only
              </div>
              <div className="text-sm font-semibold">
                {trackingLevel === 'quick' ? 'Just sets' : trackingLevel === 'training' ? 'Sets and effort' : 'Full effort'}
              </div>
            </div>
            <button type="button" onClick={() => setTrackingSheetOpen(true)} className="text-xs font-bold text-sky-300">
              Change
            </button>
          </div>
        )}

        {exercises.length === 0 && !cardioMode ? (
          <div className="flex flex-col items-center py-16 text-center">
            <p className="text-sm text-[var(--text-muted)]">Add your first exercise to start logging.</p>
            <PrimaryButton className="mt-4" onClick={() => setPickerOpen(true)}>
              Add exercise
            </PrimaryButton>
          </div>
        ) : (
          <div className="space-y-4">
            {exercises.map((ex, i) => (
              <ExerciseSessionCard
                key={ex.key}
                exercise={ex}
                index={i}
                imperial={imperial}
                workouts={workouts}
                showAdvancedDefault={trackingLevel !== 'quick'}
                showRpe={trackingLevel !== 'quick'}
                showRir={trackingLevel === 'full'}
                onChange={(next) =>
                  setExercises((prev) => prev.map((e) => (e.key === ex.key ? next : e)))
                }
                onRemove={() => setExercises((prev) => prev.filter((e) => e.key !== ex.key))}
                onSetComplete={() => {
                  if (trainingPrefs.autoRestTimer) setRestActive(true);
                }}
              />
            ))}
          </div>
        )}

        {saveError ? <p className="mt-3 text-sm text-red-300">{saveError}</p> : null}
      </div>

      <footer className="shrink-0 border-t border-white/8 bg-[#070A12]/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg flex-col gap-2">
          {!cardioMode ? (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="w-full rounded-2xl border border-white/10 bg-white/[0.04] py-3 text-sm font-semibold text-[var(--text-secondary)]"
            >
              + Add exercise
            </button>
          ) : null}
          <PrimaryButton fullWidth disabled={!canFinish} onClick={openFinishSheet}>
            {saving ? 'Saving…' : 'Finish workout'}
          </PrimaryButton>
        </div>
      </footer>

      <BottomSheet open={finishSheetOpen} onClose={() => setFinishSheetOpen(false)} title="Finish workout">
        <div className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">
            Confirm duration and session details before saving.
          </p>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--text-muted)]">Session name</span>
            <input
              value={workoutName}
              onChange={(e) => setWorkoutName(e.target.value)}
              placeholder="Workout name"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-base text-[var(--text-primary)] outline-none"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--text-muted)]">Activity</span>
            <select
              value={activityType}
              onChange={(e) => applyActivityType(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-base text-[var(--text-primary)] outline-none"
            >
              {WORKOUT_ACTIVITY_TYPES.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium text-[var(--text-muted)]">Started</span>
              <div className="mt-1">
                <StartedAtFields value={sessionStartedLocal} onChange={setSessionStartedLocal} />
              </div>
            </div>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-[var(--text-muted)]">Duration (min)</span>
              <input
                inputMode="numeric"
                value={durationMinutesStr}
                onChange={(e) => setDurationMinutesStr(e.target.value)}
                placeholder={String(elapsed)}
                className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-base text-[var(--text-primary)] outline-none"
              />
            </label>
            {cardioMode ? (
              <div className="grid grid-cols-2 gap-2">
                <label className="block space-y-1">
                  <span className="text-xs font-medium text-[var(--text-muted)]">
                    Distance ({imperial ? 'mi' : 'km'})
                  </span>
                  <input
                    inputMode="decimal"
                    value={cardioSegments[0]?.distance ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCardioSegments((prev) => {
                        const next = prev.length ? [...prev] : [emptySegment(activityType)];
                        next[0] = { ...next[0], distance: v };
                        return next;
                      });
                    }}
                    placeholder={imperial ? '3.1' : '5'}
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-base text-[var(--text-primary)] outline-none"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-medium text-[var(--text-muted)]">Avg HR</span>
                  <input
                    inputMode="numeric"
                    value={cardioSegments[0]?.avgHr ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCardioSegments((prev) => {
                        const next = prev.length ? [...prev] : [emptySegment(activityType)];
                        next[0] = { ...next[0], avgHr: v };
                        return next;
                      });
                    }}
                    placeholder="148"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-base text-[var(--text-primary)] outline-none"
                  />
                </label>
              </div>
            ) : null}
          </div>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--text-muted)]">Session notes</span>
            <textarea
              value={sessionNotes}
              onChange={(e) => setSessionNotes(e.target.value)}
              rows={3}
              placeholder="How you felt, energy, soreness, etc."
              className="w-full resize-y rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-base text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
            />
          </label>
          <p className="text-xs text-[var(--text-muted)]">Estimated burn: ~{previewKcal} kcal</p>
          {durationWarns.length ? (
            <ul className="text-xs text-amber-200">
              {durationWarns.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          ) : null}
          {saveError ? <p className="text-sm text-red-300">{saveError}</p> : null}
          <PrimaryButton fullWidth disabled={saving} onClick={() => void finishWorkout()}>
            {saving ? 'Saving…' : 'Save workout'}
          </PrimaryButton>
        </div>
      </BottomSheet>

      <TrackingLevelSheet
        open={askTracking || trackingSheetOpen}
        activityType={activityType}
        onPick={(lvl) => void saveTracking(lvl, activityType)}
        onSkip={() => {
          if (askTracking) void saveTracking('quick', activityType);
          else setTrackingSheetOpen(false);
        }}
      />

      <ExercisePickerSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={addExerciseByName}
        builtIn={exerciseBuiltIn}
        custom={exerciseCustom}
        recent={recentExercises}
      />

      <RestTimerHost
        active={restActive}
        seconds={trainingPrefs.restTimerSeconds}
        onDismiss={() => setRestActive(false)}
        bottomOffset="var(--workout-session-footer-clearance)"
      />
    </div>
  );
}
